import { MEDIUM_TYPE_PHYSICAL } from '@/prisma/schemaConstants'
import CartSummary from '@/src/common/components/elements/Checkout/CartSummary'
import CheckoutHeader from '@/src/common/components/elements/Checkout/CheckoutHeader'
import CartBoxItem from '@/src/common/components/elements/Layout/CartBoxItem'
import CartItem from '@/src/common/components/elements/Layout/CartItem'
import Button from '@/src/common/components/marketing/buttons/Button'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { ICart, IDelivery, ILineItem } from '../../cart/ICart'
import { addressValidationSchemaWithDelivery } from '../schemas'
import { buildPhysicalDeliveries } from '../utilities/buildPhysicalDeliveries'
import { splitDeliveries } from '../utilities/getPhysicalDeliveries'
import ShippingForm from './ShippingForm'
import {
    BackButton,
    Container,
    DeliveryList,
    MultiShipmentActionText,
    OrderTotal,
    ShippingTitle,
} from './ShippingStepStyles'
import {
    eligibleForMultipleShipments,
    getCarrierFromOption,
    getOptionsFromDelivery,
} from './utilities'
import { isMilitaryBaseAddressWithMultiplePlastics } from '@/src/modules/checkout/utilities/isMilitaryBaseAddressWithMultiplePlastics'
import {
    FullWidthWrapper,
    HalfContentContainer,
    LeftContentWrapper,
    RightContentWrapper,
} from '@/src/common/components/elements/StyledComponents'
import { BaseContainer } from '@/src/modules/cart/StyledComponents'
import { sendBasicEvent } from '@/src/common/utilities/gtag'
import { TPartnerPayload } from '@/src/modules/partners/types'

function determineBoxData(delivery: IDelivery) {
    if (delivery?.line_items[0]?.customization_data?.box_uuid) {
        const boxTotalLoadValue: number =
            Number(delivery.line_items[0].denomination) *
            delivery.resolved_data.items_count
        const boxTotalFees: number =
            Number(delivery.line_items[0].resolved_data.fees_total) *
            delivery.resolved_data.items_count
        return {
            box_uuid:
                delivery?.line_items[0]?.customization_data?.box_uuid ?? '',
            items: delivery.line_items,
            box_total_price: boxTotalLoadValue + boxTotalFees,
            box_total_load_value: boxTotalLoadValue,
        }
    }
    return null
}

// this is used to determine which alert message to show when trying to ship
// a box to a military base
function determineIfAllBoxes(delivery: IDelivery) {
    return !delivery.line_items.find(
        (item: ILineItem) => !item.customization_data?.box_uuid
    )
}

export interface IShippingStep {
    cartData?: ICart
    addDeliveriesToCart: Function
    removeItemFromCart: Function
    currentStep: number
    partnerData?: TPartnerPayload
}

export default function ShippingStep({
    cartData,
    addDeliveriesToCart,
    removeItemFromCart,
    currentStep,
    partnerData,
}: IShippingStep) {
    const existingDeliveries =
        cartData?.cart_contents?.deliveries.filter((delivery) => {
            return delivery.line_items.every((lineItem) => {
                return (
                    lineItem.product.resolved_data?.product_medium
                        .medium_type === MEDIUM_TYPE_PHYSICAL
                )
            })
        }) ?? []

    const [newDeliveries, setNewDeliveries] =
        useState<IDelivery[]>(existingDeliveries)

    const [deliveryOptions, setDeliveryOptions] = useState<any[]>([])
    const [isMultipleShipments, setIsMultipleShipments] = useState<boolean>(
        existingDeliveries.length > 1
    )

    const [history, setHistory] = useState<{ type: string; value: any }[]>([])
    const [formErrors, setFormErrors] = useState<any>({})
    const [analyticSent, setAnalyticSent] = useState<boolean>(false)
    const router = useRouter()

    const deliveries =
        isMultipleShipments || !newDeliveries.length
            ? newDeliveries
            : [newDeliveries[0]]

    const validateDeliveryForm = async () => {
        const errors: any = {}

        for await (const [index, delivery] of newDeliveries.entries()) {
            const { address } = delivery.delivery_details

            const formData = {
                name: address?.name,
                address1: address?.address1,
                city: address?.city,
                state: address?.state,
                zip: address?.zip,
                delivery: delivery.delivery_option?.delivery_option_uuid,
            }

            try {
                await addressValidationSchemaWithDelivery.isValid(formData, {
                    abortEarly: false,
                })

                await addressValidationSchemaWithDelivery.validate(formData, {
                    abortEarly: false,
                })

                delete errors[index]
            } catch (e: any) {
                errors[index] = e.inner
            }
        }

        setFormErrors(errors)
        return !Object.keys(errors).length
    }

    const toggleMultiDeliveries = () => {
        setHistory((value) => [
            ...value,
            { type: 'multi-toggle', value: isMultipleShipments },
        ])
        setIsMultipleShipments((x) => !x)
    }

    const goBack = () => {
        const previous = history.pop()

        if (previous?.type === 'multi-shipment') {
            setIsMultipleShipments(previous.value)
        } else {
            router.push('/')
        }
    }

    const submitDeliveryData = async () => {
        const validForm = await validateDeliveryForm()

        if (validForm) {
            addDeliveriesToCart(newDeliveries)
        }
    }

    const onFormChange = ({
        name,
        address,
        deliveryMethodUuid,
        index,
    }: any) => {
        if (formErrors[index]) {
            if (name) {
                formErrors[index] = formErrors[index].filter(
                    (err: any) => err.path !== 'name'
                )
            }

            Object.keys(address).forEach((field) => {
                if (address[field]) {
                    formErrors[index] = formErrors[index].filter(
                        (err: any) => err.path !== field
                    )
                }
            })

            if (deliveryMethodUuid) {
                formErrors[index] = formErrors[index].filter(
                    (err: any) => err.path !== 'delivery'
                )
            }

            setFormErrors({ ...formErrors })
        }

        setNewDeliveries((cur) => {
            cur[index] = {
                ...cur[index],
                delivery_details: {
                    ...(cur[index]?.delivery_details ?? {}),
                    address: {
                        ...address,
                        name,
                    },
                },
                delivery_option: {
                    ...(cur[index]?.delivery_option ?? {}),
                    delivery_option_uuid: deliveryMethodUuid,
                },
            }
            return [...cur]
        })
    }

    useEffect(() => {
        if (!analyticSent) {
            sendBasicEvent('checkout_shipping_step')
            setAnalyticSent(true)
        }
    }, [analyticSent])

    useEffect(() => {
        const { physicalDeliveries } = splitDeliveries(cartData)

        const deliveries = buildPhysicalDeliveries(
            physicalDeliveries,
            isMultipleShipments
        )

        const deliveryOptions = getOptionsFromDelivery(
            deliveries[0],
            isMultipleShipments
        )

        setFormErrors({})
        setDeliveryOptions(deliveryOptions)
        setNewDeliveries(deliveries)

        if (!isMultipleShipments && deliveries.length > 1) {
            setIsMultipleShipments(true)
        }
    }, [isMultipleShipments, cartData])

    return (
        <Container>
            <CheckoutHeader
                currentStep={currentStep}
                hasPlastic={true}
                partnerData={partnerData}
            />
            <FullWidthWrapper flexDirection={'column'}>
                {deliveries.map((delivery: any, index: number) => (
                    <DeliveryList
                        key={delivery.uuid}
                        border={isMultipleShipments ? 'bottom' : undefined}
                    >
                        <HalfContentContainer background={'lightest'}>
                            <LeftContentWrapper>
                                {index === 0 && (
                                    <>
                                        <BackButton onClick={() => goBack()} />

                                        <ShippingTitle />
                                    </>
                                )}

                                {index === 0 &&
                                    eligibleForMultipleShipments(cartData) && (
                                        <MultiShipmentActionText
                                            multiple={isMultipleShipments}
                                            onClick={() =>
                                                toggleMultiDeliveries()
                                            }
                                        />
                                    )}

                                <ShippingForm
                                    shippingAddress={
                                        delivery.delivery_details?.address
                                    }
                                    deliveryOptions={deliveryOptions}
                                    selectedDeliveryOption={
                                        delivery.delivery_option
                                            ?.delivery_option_uuid
                                    }
                                    hasOnlyBoxes={determineIfAllBoxes(delivery)}
                                    allowUsps={
                                        !determineIfAllBoxes(delivery) &&
                                        !isMilitaryBaseAddressWithMultiplePlastics(
                                            {
                                                lineItems: delivery.line_items,
                                                state:
                                                    newDeliveries[index]
                                                        ?.delivery_details
                                                        ?.address?.state ?? '',
                                            }
                                        )
                                    }
                                    formErrors={formErrors[index]}
                                    onChange={({
                                        name,
                                        address,
                                        deliveryMethodUuid,
                                    }) =>
                                        onFormChange({
                                            name,
                                            address,
                                            deliveryMethodUuid,
                                            index,
                                        })
                                    }
                                    setIsMultipleShipments={
                                        setIsMultipleShipments
                                    }
                                />

                                {(!isMultipleShipments ||
                                    index === newDeliveries.length - 1) && (
                                    <Button
                                        onClick={() => submitDeliveryData()}
                                        label={'Continue'}
                                        showArrow={true}
                                        width={'wide'}
                                        size={'md'}
                                        style={{ marginTop: 10 }}
                                    />
                                )}
                            </LeftContentWrapper>
                        </HalfContentContainer>
                        <HalfContentContainer>
                            <RightContentWrapper>
                                {!isMultipleShipments ? (
                                    <CartSummary
                                        cartData={cartData}
                                        removeItemFromCart={removeItemFromCart}
                                        showPromoCodeInput={false}
                                        partnerData={partnerData}
                                    />
                                ) : (
                                    <BaseContainer>
                                        {determineBoxData(delivery) ? (
                                            <CartBoxItem
                                                box={
                                                    determineBoxData(delivery)!
                                                }
                                                removeItemFromCart={
                                                    removeItemFromCart
                                                }
                                                hideBottomBorder={
                                                    isMultipleShipments
                                                }
                                                shipping={
                                                    delivery.delivery_details
                                                        .address?.address1 && {
                                                        address:
                                                            delivery
                                                                .delivery_details
                                                                .address,
                                                        carrier:
                                                            getCarrierFromOption(
                                                                {
                                                                    options:
                                                                        deliveryOptions,
                                                                    uuid: delivery
                                                                        .delivery_option
                                                                        .delivery_option_uuid,
                                                                }
                                                            ),
                                                    }
                                                }
                                            />
                                        ) : (
                                            delivery.line_items.map(
                                                (item: any) => (
                                                    <CartItem
                                                        key={
                                                            item.line_item_uuid
                                                        }
                                                        partnerData={
                                                            partnerData
                                                        }
                                                        cartLineItem={item}
                                                        hideBottomBorder={
                                                            isMultipleShipments
                                                        }
                                                        removeItemFromCart={
                                                            removeItemFromCart
                                                        }
                                                        shipping={
                                                            delivery
                                                                .delivery_details
                                                                ?.address
                                                                ?.address1 && {
                                                                address:
                                                                    delivery
                                                                        .delivery_details
                                                                        .address,
                                                                option: getCarrierFromOption(
                                                                    delivery
                                                                        .delivery_option
                                                                        .delivery_option_uuid
                                                                ),
                                                            }
                                                        }
                                                    />
                                                )
                                            )
                                        )}

                                        {newDeliveries.length - 1 === index && (
                                            <OrderTotal
                                                total={
                                                    cartData?.resolved_data
                                                        ?.order_total
                                                }
                                            />
                                        )}
                                    </BaseContainer>
                                )}
                            </RightContentWrapper>
                        </HalfContentContainer>
                    </DeliveryList>
                ))}
            </FullWidthWrapper>
        </Container>
    )
}
